import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { StandardURL } from '../services/constants';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --bg: #0a0a0a;
    --surface: #111111;
    --surface2: #1a1a1a;
    --border: #2a2a2a;
    --accent: #e8ff3c;
    --accent-dim: rgba(232,255,60,0.10);
    --text: #f0f0f0;
    --muted: #666666;
    --red: #ff4545;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-page {
    min-height: 100vh;
    background: var(--bg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    padding: 2rem 1rem;
    position: relative;
    overflow: hidden;
  }
  .auth-page::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(var(--border) 1px, transparent 1px),
      linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 48px 48px;
    opacity: 0.35;
    pointer-events: none;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 2.8rem 2.5rem;
    width: 100%;
    max-width: 460px;
    box-shadow: 0 0 60px rgba(232,255,60,0.04);
  }

  .auth-logo {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.2rem;
    letter-spacing: 0.1em;
    color: var(--accent);
    text-align: center;
    display: block;
    margin-bottom: 0.3rem;
  }
  .auth-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.6rem;
    letter-spacing: 0.06em;
    color: var(--text);
    text-align: center;
    margin-bottom: 0.4rem;
  }
  .auth-subtitle {
    text-align: center;
    color: var(--muted);
    font-size: 0.82rem;
    margin-bottom: 2rem;
  }
  .auth-divider {
    height: 1px;
    background: var(--border);
    margin: 1.6rem 0;
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    margin-bottom: 1.2rem;
  }
  .field-wrap {
    position: relative;
  }
  .auth-input {
    width: 100%;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 6px;
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.88rem;
    padding: 0.72rem 1rem;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .auth-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-dim);
  }
  .auth-input::placeholder { color: var(--muted); }
  .auth-input.has-icon { padding-right: 2.8rem; }

  .eye-btn {
    position: absolute;
    right: 0.8rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--muted);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color 0.2s;
  }
  .eye-btn:hover { color: var(--text); }

  .field-error {
    font-size: 0.75rem;
    color: var(--red);
    margin-top: 0.3rem;
    padding-left: 0.2rem;
  }

  .terms-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 1.4rem;
  }
  .terms-row input[type="checkbox"] {
    accent-color: var(--accent);
    width: 15px;
    height: 15px;
    cursor: pointer;
    flex-shrink: 0;
  }
  .terms-row label {
    font-size: 0.8rem;
    color: var(--muted);
    cursor: pointer;
    line-height: 1.5;
  }
  .terms-row a {
    color: var(--accent);
    text-decoration: none;
    font-weight: 500;
  }
  .terms-row a:hover { text-decoration: underline; }

  .auth-btn {
    width: 100%;
    background: var(--accent);
    color: #000;
    border: none;
    border-radius: 6px;
    padding: 0.78rem 1.5rem;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.1rem;
    letter-spacing: 0.08em;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: opacity 0.2s, transform 0.15s;
  }
  .auth-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  .auth-btn:disabled { background: var(--surface2); color: var(--muted); cursor: not-allowed; }

  .auth-footer-text {
    text-align: center;
    margin-top: 1.5rem;
    font-size: 0.8rem;
    color: var(--muted);
  }
  .auth-footer-text a {
    color: var(--accent);
    text-decoration: none;
    font-weight: 500;
  }
  .auth-footer-text a:hover { text-decoration: underline; }
`;

const Registration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    termsAndCondition: false,
  });
  const [errors, setErrors] = useState({ password: "", confirmPassword: "" });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    if (name === "password") validatePassword(value);
    else if (name === "confirmPassword") validateConfirmPassword(value, formData.password);
  };

  const validatePassword = (password) => {
    const minLength = 8, maxLength = 64;
    const regexNumber = /\d/;
    const regexLowerCase = /[a-z]/;
    const regexUpperCase = /[A-Z]/;
    const regexSpecialChar = /[@#$%^&*(),.?":{}|<>]/;
    let errorMessage = "";
    if (password.length < minLength || password.length > maxLength) {
      errorMessage = `Password must be between ${minLength}-${maxLength} characters.`;
    } else if (!regexNumber.test(password)) {
      errorMessage = "Password must contain at least one number.";
    } else if (!regexLowerCase.test(password)) {
      errorMessage = "Password must contain at least one lowercase letter.";
    } else if (!regexUpperCase.test(password)) {
      errorMessage = "Password must contain at least one uppercase letter.";
    } else if (!regexSpecialChar.test(password)) {
      errorMessage = "Password must contain at least one special character.";
    }
    setErrors((prev) => ({ ...prev, password: errorMessage }));
    return errorMessage === "";
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    const errorMessage = confirmPassword !== password ? "Passwords do not match." : "";
    setErrors((prev) => ({ ...prev, confirmPassword: errorMessage }));
    return errorMessage === "";
  };

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!confirmPasswordVisible);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isPasswordValid = validatePassword(formData.password);
    const isConfirmPasswordValid = validateConfirmPassword(formData.confirmPassword, formData.password);
    if (!isPasswordValid || !isConfirmPasswordValid || !formData.termsAndCondition) {
      toast.error("Please correct the errors in the form and accept terms & conditions.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${StandardURL}/iMusic/auth/register`,
        formData,
        { params: { accountType: "CUSTOMER" } }
      );
      toast.success("Account created and verified successfully!");
      setIsLoading(false);
      navigate("/");
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        toast.error("Unauthorized access.");
      } else if (error.response && error.response.status === 406) {
        toast.error("Wrong password format. Password must contain a number, capital letter, small letter, and a special character.");
      } else {
        toast.error("An error occurred, please try again later.");
      }
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <div className="auth-card">
          <span className="auth-logo">MUSICA</span>
          <h2 className="auth-title">Create Account</h2>
          <p className="auth-subtitle">Join Musica and start licensing beats today</p>
          <div className="auth-divider" />

          <form onSubmit={handleSubmit}>
            <div className="field-group">
              <div className="field-wrap">
                <input
                  id="name" name="name" type="text" autoComplete="name" required
                  className="auth-input" placeholder="Your Full Name"
                  value={formData.name} onChange={handleChange}
                />
              </div>
              <div className="field-wrap">
                <input
                  id="email" name="email" type="email" autoComplete="email" required
                  className="auth-input" placeholder="Your Email Address"
                  value={formData.email} onChange={handleChange}
                />
              </div>
              <div className="field-wrap">
                <input
                  id="phoneNumber" name="phoneNumber" type="tel" autoComplete="tel" required
                  className="auth-input" placeholder="Phone Number (e.g., +2547XXXXXXXX)"
                  value={formData.phoneNumber} onChange={handleChange}
                />
              </div>
              <div>
                <div className="field-wrap">
                  <input
                    id="password" name="password"
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="new-password" required
                    className="auth-input has-icon" placeholder="Create your password"
                    value={formData.password} onChange={handleChange}
                  />
                  <button type="button" className="eye-btn" onClick={togglePasswordVisibility}
                    aria-label={passwordVisible ? "Hide password" : "Show password"}>
                    <FontAwesomeIcon icon={passwordVisible ? faEye : faEyeSlash} style={{ width: 16, height: 16 }} />
                  </button>
                </div>
                {errors.password && <p className="field-error">{errors.password}</p>}
              </div>
              <div>
                <div className="field-wrap">
                  <input
                    id="confirmPassword" name="confirmPassword"
                    type={confirmPasswordVisible ? "text" : "password"}
                    autoComplete="new-password" required
                    className="auth-input has-icon" placeholder="Confirm your password"
                    value={formData.confirmPassword} onChange={handleChange}
                  />
                  <button type="button" className="eye-btn" onClick={toggleConfirmPasswordVisibility}
                    aria-label={confirmPasswordVisible ? "Hide password" : "Show password"}>
                    <FontAwesomeIcon icon={confirmPasswordVisible ? faEye : faEyeSlash} style={{ width: 16, height: 16 }} />
                  </button>
                </div>
                {errors.confirmPassword && <p className="field-error">{errors.confirmPassword}</p>}
              </div>
            </div>

            <div className="terms-row">
              <input
                id="terms-and-condition" name="termsAndCondition" type="checkbox"
                checked={formData.termsAndCondition} onChange={handleChange} required
              />
              <label htmlFor="terms-and-condition">
                I agree to the{" "}
                <a href="#">Terms and Conditions</a>
              </label>
            </div>

            <button
              type="submit"
              className="auth-btn"
              disabled={isLoading || !!errors.password || !!errors.confirmPassword || !formData.termsAndCondition}
            >
              {isLoading ? (
                <Oval height={22} width={22} color="#000" visible={true} ariaLabel="oval-loading" secondaryColor="rgba(0,0,0,0.4)" strokeWidth={5} strokeWidthSecondary={5} />
              ) : (
                "Register Account"
              )}
            </button>
          </form>

          <p className="auth-footer-text">
            Already have an account?{" "}
            <a href="/login">Log In</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default Registration;